import { useCallback, useMemo } from "react";
import { trpc } from "../../../lib/api/trpc";
import {
  maxFileSizeBytesFor as maxFileSizeBytesForMime,
  resolveClientMimeType,
  type SupportedFormat,
} from "../utils/fileType";

const FALLBACK_FORMATS: SupportedFormat[] = [];
const FALLBACK_MIME_TYPES = ["application/pdf"];

/**
 * Fetch supported document formats from the backend.
 * The backend derives this from registered parsers — single source of truth.
 */
export function useSupportedFormats() {
  // @ts-ignore - tRPC type issues
  const { data, isLoading } = trpc.health.supportedFormats.useQuery(undefined, {
    staleTime: 1000 * 60 * 60, // Cache for 1 hour — this rarely changes
    cacheTime: 1000 * 60 * 60,
  });

  const formats: SupportedFormat[] = data?.formats ?? FALLBACK_FORMATS;
  const maxFileSizeMB: number = data?.maxFileSizeMB ?? 50;
  const supportedMimeTypes: string[] = useMemo(
    () =>
      formats.length > 0 ? formats.map((f) => f.mime) : FALLBACK_MIME_TYPES,
    [formats],
  );

  /** Resolve a file's supported MIME (browser type, then extension) or null. */
  const resolveMimeType = useCallback(
    (file: { name: string; type: string }) =>
      resolveClientMimeType(file, supportedMimeTypes, formats),
    [supportedMimeTypes, formats],
  );

  /** Per-type max size in bytes (text formats are capped lower than the global limit). */
  const maxFileSizeBytesFor = useCallback(
    (mime: string | null) =>
      maxFileSizeBytesForMime(mime, formats, maxFileSizeMB),
    [formats, maxFileSizeMB],
  );

  /** Checkbox/select options for admin forms, e.g. "Markdown (.md, .markdown)". */
  const formatOptions = useMemo(
    () =>
      formats.map((f) => ({
        mime: f.mime,
        label: `${f.label} (${(f.extensions ?? [f.extension]).join(", ")})`,
      })),
    [formats],
  );

  return {
    formats,
    formatOptions,
    acceptString: data?.acceptString ?? "application/pdf",
    formatsLabel: data?.formatsLabel ?? "PDF",
    maxFileSizeMB,
    supportedMimeTypes,
    resolveMimeType,
    maxFileSizeBytesFor,
    isLoading,
  };
}
